'use client';

import { useUser, EditorPreferences, DEFAULT_EDITOR_PREFERENCES, FieldVisibility, DEFAULT_FIELD_VISIBILITY } from '@/context/UserContext';
import { useState, useCallback, useMemo, useRef } from 'react';
import { apiFetch } from '@/lib/api';
import { showToast } from '@/components/ui/Toast';

export type { EditorPreferences, FieldVisibility } from '@/context/UserContext';
export { DEFAULT_EDITOR_PREFERENCES, DEFAULT_FIELD_VISIBILITY } from '@/context/UserContext';

/** Options for updatePreferences */
interface UpdatePreferencesOptions {
  /** If true, don't show toast notification (useful for automatic/silent saves) */
  silent?: boolean;
}

/**
 * Hook to manage editor preferences for the 1-click editor auto-apply
 */
export function useEditorPreferences() {
  const { user, refresh } = useUser();
  const [isSaving, setIsSaving] = useState(false);
  
  // Keep a ref to the latest preferences to avoid closure issues
  const preferencesRef = useRef<EditorPreferences>(DEFAULT_EDITOR_PREFERENCES);

  const preferences: EditorPreferences = user?.editorPreferences ?? DEFAULT_EDITOR_PREFERENCES;
  
  // Update ref whenever preferences change
  preferencesRef.current = preferences;
  
  const shouldShowPopup = !preferences.skipPopup;
  
  // Field visibility with defaults
  const fieldVisibility = useMemo<FieldVisibility>(() => {
    const visibility = preferences.fieldVisibility ?? DEFAULT_FIELD_VISIBILITY;
    console.log("👁️ [useEditorPreferences] fieldVisibility computed:", visibility);
    return visibility;
  }, [preferences.fieldVisibility]);

  /**
   * Update editor preferences in the database
   * @param newPrefs - Partial preferences to merge with existing
   * @param options - Optional settings (e.g., silent mode)
   */
  const updatePreferences = useCallback(async (
    newPrefs: Partial<EditorPreferences>,
    options?: UpdatePreferencesOptions
  ) => {
    const { silent = false } = options ?? {};
    
    console.log("📥 [useEditorPreferences] updatePreferences chamado:", {
      newPrefs,
      silent,
      hasLogoScale: 'logoScale' in newPrefs,
      logoScaleValue: (newPrefs as any).logoScale
    });
    
    setIsSaving(true);
    try {
      // Use ref to get the LATEST preferences (avoids stale closure)
      const currentPrefs = preferencesRef.current;
      const merged = { ...currentPrefs, ...newPrefs };
      
      console.log("🔀 [useEditorPreferences] Merge de preferências:", {
        currentLogoScale: (currentPrefs as any).logoScale,
        newLogoScale: (newPrefs as any).logoScale,
        mergedLogoScale: (merged as any).logoScale
      });
      
      console.log("📤 [useEditorPreferences] Enviando para API:", {
        endpoint: '/users/me/editor-preferences',
        logoScale: (merged as any).logoScale,
        logoScaleUpdatedAt: (merged as any).logoScaleUpdatedAt
      });
      
      await apiFetch('/users/me/editor-preferences', {
        method: 'PUT',
        body: JSON.stringify(merged),
      });
      
      console.log("✅ [useEditorPreferences] API respondeu com sucesso, fazendo refresh...");
      await refresh();
      console.log("🔄 [useEditorPreferences] Refresh completado");
      
      // Only show toast if not in silent mode
      if (!silent) {
        showToast('Preferências salvas com sucesso!', 'success');
      }
      return true;
    } catch (error) {
      console.error('❌ [useEditorPreferences] Error saving editor preferences:', error);
      // Always show error toast
      showToast('Erro ao salvar preferências', 'error');
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [refresh]);

  /**
   * Reset all preferences to defaults and show popup again
   */
  const resetPreferences = useCallback(async () => {
    return updatePreferences({ ...DEFAULT_EDITOR_PREFERENCES, skipPopup: false });
  }, [updatePreferences]);

  /**
   * Check if a specific preference is enabled
   */
  const isEnabled = useCallback((key: keyof EditorPreferences): boolean => {
    return preferences[key] as boolean;
  }, [preferences]);

  /**
   * Check if a specific field is visible in canvas
   */
  const isFieldVisible = useCallback((field: keyof FieldVisibility): boolean => {
    return fieldVisibility[field] ?? true;
  }, [fieldVisibility]);

  /**
   * Toggle visibility for a specific field
   */
  const toggleFieldVisibility = useCallback(async (field: keyof FieldVisibility) => {
    const newVisibility = {
      ...fieldVisibility,
      [field]: !fieldVisibility[field]
    };
    return updatePreferences({ fieldVisibility: newVisibility });
  }, [fieldVisibility, updatePreferences]);

  return {
    preferences,
    fieldVisibility,
    shouldShowPopup,
    isSaving,
    updatePreferences,
    resetPreferences,
    isEnabled,
    isFieldVisible,
    toggleFieldVisibility,
  };
}
